import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import useIndivdiualAkiyaHook from "../individualAkiya/useIndividualAkiyaHook";
import AkiyaGallery from "./AkiyaGallery";
import Heart from "../Svgs/Heart";
import MapIComponent from "./MapIndividual";
import Loader from "../Loader/Loader";
import { FaLinkedin, FaTwitter, FaWhatsapp, FaFacebook, FaShareAlt, FaCopy } from "react-icons/fa";

import QueryForm from "./QueryForm";
import HeaderQuery from "./HeaderQuery";
import AgentCard from "./AgentCard";

// Utility functions
const cleanAreaValue = (value) => {
  if (!value) return "N/A";
  const numberMatch = value.match(/[\d,.]+/);
  if (!numberMatch) return value;
  const number = parseFloat(numberMatch[0].replace(/,/g, ""));
  return isNaN(number) ? value : `${number} m²`;
};

const parsePrice = (priceString) => {
  if (!priceString || typeof priceString !== "string") return 0;
  let cleanedPrice = priceString.toLowerCase();

  if (cleanedPrice.includes("million") || cleanedPrice.includes("百万")) {
    cleanedPrice = cleanedPrice.replace(/million|百万/gi, "");
    return parseFloat(cleanedPrice.replace(/[^0-9.]/g, "")) * 1_000_000;
  }

  if (cleanedPrice.includes("billion") || cleanedPrice.includes("十億")) {
    cleanedPrice = cleanedPrice.replace(/billion|十億/gi, "");
    return parseFloat(cleanedPrice.replace(/[^0-9.]/g, "")) * 1_000_000_000;
  }

  return parseFloat(cleanedPrice.replace(/[^0-9.]/g, ""));
};

const headingData = {
  heading: "Interested in this Akiya?",
  info: "Akiya 2.0 can assist you from purchase to renovation and investments. Contact us and our friendly staff will come back to you as soon as possible.",
};

const IndividualAkiya = memo(() => {
  const {
    individualAkiya,
    tableData,
    municipality,
    property_no,
    handleToggleFavorite,
    isFavorite,
  } = useIndivdiualAkiyaHook();



  const [currentUrl, setCurrentUrl] = useState("");
  const [isAkiya, setIsAkiya] = useState(true);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Get current URL for sharing
  useEffect(() => {
    if (typeof window !== "undefined") {
      setCurrentUrl(window.location.href);
    }
  }, []);

  // Determine property type (Akiya vs Rental)
  useEffect(() => {
    let isAkiyaProperty = false;

    if (tableData?.["Empty or Not"]) {
      const value = tableData["Empty or Not"].toLowerCase();
      const keywords = ["akiya", "buy", "sale", "purchase"];
      isAkiyaProperty = keywords.some((k) => value.includes(k));
    }

    if (tableData?.["Price"]) {
      const price = parsePrice(tableData["Price"]);
      if (price > 300000) isAkiyaProperty = true;
    }

    setIsAkiya(isAkiyaProperty);
  }, [tableData]);

  // Memoized theme colors
  const themeColors = useMemo(() => ({
    primary: isAkiya ? "#10c9c0" : "#3B82F6",
    hover: isAkiya ? "#0eb1a9" : "#2563EB",
    light: isAkiya ? "#10c9c020" : "#3B82F620",
    border: isAkiya ? "var(--medium-sea-green)" : "#3B82F6"
  }), [isAkiya]);

  // Enhanced sharing links
  const shareLinks = useMemo(() => [
    {
      name: "LinkedIn",
      link: `https://www.linkedin.com/shareArticle?url=${encodeURIComponent(currentUrl)}&title=${encodeURIComponent(`${municipality} Akiya 2.0 Listing`)}`,
      icon: <FaLinkedin className="w-5 h-5" />,
      color: "#0077B5"
    },
    {
      name: "Twitter",
      link: `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(`Check out this amazing property in ${municipality}!`)}`,
      icon: <FaTwitter className="w-5 h-5" />,
      color: "#1DA1F2"
    },
    {
      name: "WhatsApp",
      link: `https://wa.me/?text=${encodeURIComponent(`Check out this property: ${currentUrl}`)}`,
      icon: <FaWhatsapp className="w-5 h-5" />,
      color: "#25D366"
    },
    {
      name: "Facebook",
      link: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
      icon: <FaFacebook className="w-5 h-5" />,
      color: "#1877F2"
    }
  ], [currentUrl, municipality]);

  // Copy to clipboard function
  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  }, [currentUrl]);

  if (!individualAkiya && !tableData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader big={true} />
          <p className="mt-4 text-gray-600">Loading property details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-64 h-64 bg-[#10c9c0]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 -right-32 w-64 h-64 bg-[#0eb1a9]/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative flex justify-center">
        <div className="max-w-7xl w-full mx-4 lg:mx-8 py-8">

          {/* Enhanced Gallery Section */}
          <section className="mb-12">
            {individualAkiya?.Images ? (
              <AkiyaGallery images={individualAkiya.Images} />
            ) : (
              <div className="h-96 bg-gray-100 rounded-xl flex items-center justify-center">
                <Loader big={false} />
              </div>
            )}
          </section>

          {/* Enhanced Header Section */}
          <section className="mb-8">
            <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/50 p-6 lg:p-8">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">

                {/* Property Information */}
                <div className="flex-1 space-y-4">
                  <div>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
                      {municipality} Property
                    </h1>
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                      <p className="text-lg text-gray-600">
                        Listing No. <span className="font-semibold">{property_no}</span>
                      </p>

                      {/* Property Type Badge */}
                      <span
                        className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold text-white shadow-md"
                        style={{ backgroundColor: themeColors.primary }}
                      >
                        {isAkiya ? "🏠 Akiya Property" : "🏘️ Rental Property"}
                      </span>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  {tableData && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4">
                      {tableData.Price && (
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-lg font-bold text-gray-900">{tableData.Price}</div>
                          <div className="text-sm text-gray-600">Price</div>
                        </div>
                      )}
                      {tableData["Area of Land"] && (
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-lg font-bold text-gray-900">{cleanAreaValue(tableData["Area of Land"])}</div>
                          <div className="text-sm text-gray-600">Land Area</div>
                        </div>
                      )}
                      {tableData["Area of House"] && (
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-lg font-bold text-gray-900">{cleanAreaValue(tableData["Area of House"])}</div>
                          <div className="text-sm text-gray-600">House Area</div>
                        </div>
                      )}
                      {tableData["Year of Construction"] && (
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-lg font-bold text-gray-900">{tableData["Year of Construction"]}</div>
                          <div className="text-sm text-gray-600">Built</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-row lg:flex-col items-center lg:items-end gap-4">

                  {/* Favorite Button */}
                  <button
                    onClick={handleToggleFavorite}
                    className={`group flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${isFavorite
                        ? 'text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    style={isFavorite ? { backgroundColor: themeColors.primary } : {}}
                  >
                    <div className="w-6 h-6">
                      <Heart />
                    </div>
                    <span className="hidden sm:inline">
                      {isFavorite ? "Favorited" : "Add to Favorites"}
                    </span>
                  </button>

                  {/* Share Button */}
                  <div className="relative">
                    <button
                      onClick={() => setShowShareMenu(!showShareMenu)}
                      className="flex items-center gap-3 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105"
                    >
                      <FaShareAlt className="w-5 h-5" />
                      <span className="hidden sm:inline">Share</span>
                    </button>

                    {/* Enhanced Share Dropdown */}
                    {showShareMenu && (
                      <>
                        {/* Backdrop */}
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setShowShareMenu(false)}
                        ></div>

                        {/* Share Menu */}
                        <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 p-6 z-20">
                          <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Share this property</h3>

                            {/* Share Links Grid */}
                            <div className="grid grid-cols-2 gap-3">
                              {shareLinks.map((link) => (
                                <a
                                  key={link.name}
                                  href={link.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 group"
                                >
                                  <div style={{ color: link.color }}>
                                    {link.icon}
                                  </div>
                                  <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{link.name}</span>
                                </a>
                              ))}
                            </div>

                            {/* Copy Link Section */}
                            <div className="pt-4 border-t border-gray-100">
                              <button
                                onClick={copyToClipboard}
                                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-200 ${copySuccess
                                    ? 'bg-green-50 text-green-700'
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                  }`}
                              >
                                <FaCopy className="w-5 h-5" />
                                <span className="text-sm font-medium">
                                  {copySuccess ? "Link copied!" : "Copy link"}
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Enhanced Map Section */}
          {individualAkiya?.Coordinates && (
            <section className="mb-12">
              <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/50 overflow-hidden">
                <div
                  className="px-6 py-4 text-white text-xl font-semibold flex items-center gap-3"
                  style={{ backgroundColor: themeColors.primary }}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Property Location
                </div>
                <div className="h-96">
                  <MapIComponent
                    apiKey="AIzaSyDhd_bCluPIx2cSHS7pGd5wVPkIRU7rpxQ"
                    coordinates={individualAkiya.Coordinates}
                  />
                </div>
              </div>
            </section>
          )}

          {/* Enhanced Property Details Table */}
          <section className="mb-12">
            <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/50 overflow-hidden">
              <div
                className="px-6 py-4 text-white text-xl font-semibold flex items-center gap-3"
                style={{ backgroundColor: themeColors.primary }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Property Details
              </div>

              {tableData && (
                <div className="divide-y divide-gray-200/50">
                  {Object.entries(tableData).map(([field, value], index) => {
                    let displayField = field;
                    let displayValue = value;

                    // Field transformations
                    if (field === "Empty or Not") {
                      displayField = "Property Type";
                      if (tableData["Price"] && parsePrice(tableData["Price"]) > 500000) {
                        displayValue = "Akiya (Purchase)";
                      }
                    }

                    // Clean area values
                    const fieldLower = field.toLowerCase();
                    if (fieldLower.includes("area") || fieldLower.includes("size") || fieldLower.includes("land")) {
                      displayValue = cleanAreaValue(value);
                    }

                    return (
                      <div
                        key={field}
                        className="flex flex-col sm:flex-row gap-4 p-4 sm:p-6 hover:bg-gray-50/50 transition-colors duration-200"
                      >
                        <div className="sm:w-1/3 font-semibold text-gray-700 flex items-center">
                          {displayField}
                        </div>
                        <div className="sm:w-2/3 text-gray-900">
                          {field === "Link to Official page" && value !== "N/A" ? (
                            <button
                              onClick={() => window.open(value, "_blank")}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-md"
                              style={{ backgroundColor: themeColors.primary }}
                            >
                              <span>Visit Official Site</span>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </button>
                          ) : (
                            <p className="text-gray-900 leading-relaxed">{displayValue}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          {/* Property Description Section */}
          {individualAkiya?.description && (
            <section className="mb-12">
              <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/50 overflow-hidden">
                <div
                  className="px-6 py-4 text-white text-xl font-semibold flex items-center gap-3"
                  style={{ backgroundColor: themeColors.primary }}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  About This Property
                </div>
                <div className="p-6 lg:p-8">
                  <div className="prose prose-gray max-w-none">
                    {/* <p className="text-sm text-gray-500 mb-4 italic">
                      Property details provided by listing agent
                    </p> */}
                    <p className="text-gray-700 leading-relaxed text-base lg:text-lg whitespace-pre-wrap">
                      {individualAkiya.description}
                    </p>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Agent Section */}
          {individualAkiya?.agentId && (
            <section className="mb-12">
              <AgentCard
                agentId={individualAkiya.agentId}
                themeColors={themeColors}
                onViewProfile={(agentId) => {
                  // Optional: Custom handler for profile viewing
                  // If not provided, it will use default behavior
                  window.open(`/agent/${agentId}`, '_blank');
                }}
              />
            </section>
          )}

          {/* Enhanced Contact Section */}
          <section className="mb-12">
            <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/50 p-6 lg:p-8">
              <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
                <div className="space-y-6">
                  <HeaderQuery data={headingData} />
                </div>
                <div className="space-y-6">
                  <QueryForm isAkiya={isAkiya} />
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
});

IndividualAkiya.displayName = 'IndividualAkiya';

export default IndividualAkiya;